import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { EyeOff, Eye, Flag, Image as ImageIcon } from "lucide-react";
import { api, fmtError } from "@/lib/api";
import { AuthImage, Lightbox } from "@/components/AuthImage";
import { fmtDate } from "@/constants/labels";

const HideModal = ({ photo, onCancel, onConfirm }) => {
  const [motif, setMotif] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="card p-4 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="hide-modal">
        <h3 className="font-semibold text-[#1F2933]">Masquer la photo</h3>
        <p className="text-xs text-[#3E4C59] mt-1">Le motif est notifié à l'artisan. Le masquage est réversible.</p>
        <textarea className="textarea mt-3" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Motif (obligatoire)" data-testid="hide-motif" />
        <div className="flex justify-end gap-2 mt-3">
          <button className="btn-ghost" onClick={onCancel}>Annuler</button>
          <button className="btn-primary" disabled={motif.trim().length < 3} onClick={() => onConfirm(motif)} data-testid="hide-submit">Masquer</button>
        </div>
      </div>
    </div>
  );
};

const PhotoCard = ({ p, onHide, onUnhide, onOpen }) => (
  <div className="card p-3 flex gap-3" data-testid={`moderation-row-${p.id}`}>
    <AuthImage url={p.thumb_url} alt={p.legende} onClick={onOpen} className="w-28 h-28 object-cover rounded-[4px] shrink-0" testId={`moderation-thumb-${p.id}`} />
    <div className="flex-1 min-w-0 text-sm">
      <div className="font-semibold text-[#1F2933]">{p.user_nom || p.user_id}</div>
      {p.legende && <div className="text-xs text-[#3E4C59] italic">« {p.legende} »</div>}
      {p.hidden ? (
        <div className="text-xs text-[#B91C1C] mt-1 inline-flex items-center gap-1"><EyeOff size={12} /> Masquée le {fmtDate(p.hidden_at)} : {p.hidden_motif}</div>
      ) : (
        <ul className="mt-1 space-y-0.5">
          {(p.reports || []).map((r, i) => (
            <li key={i} className="text-xs text-[#7C3E00] inline-flex items-start gap-1"><Flag size={12} className="mt-0.5 shrink-0" /> {r.motif} <span className="text-[#94A3B8]">— {fmtDate(r.at)}</span></li>
          ))}
        </ul>
      )}
    </div>
    <div className="shrink-0">
      {p.hidden
        ? <button className="btn-outline text-xs" onClick={onUnhide} data-testid={`moderation-unhide-${p.id}`}><Eye size={12} /> Rétablir</button>
        : <button className="btn-outline text-xs text-[#C0392B]" onClick={onHide} data-testid={`moderation-hide-${p.id}`}><EyeOff size={12} /> Masquer</button>}
    </div>
  </div>
);

export const AdminPhotoModeration = () => {
  const [data, setData] = useState({ pending: [], hidden: [], queue_size: 0 });
  const [hiding, setHiding] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get("/admin/profile-media/queue"); setData(r.data); }
    catch (err) { toast.error(fmtError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const hide = async (motif) => {
    try { await api.post(`/admin/profile-media/photos/${hiding.id}/hide`, { motif }); toast.success("Photo masquée — l'artisan est notifié"); setHiding(null); load(); }
    catch (err) { toast.error(fmtError(err)); }
  };
  const unhide = async (p) => {
    try { await api.post(`/admin/profile-media/photos/${p.id}/unhide`); toast.success("Photo rétablie"); load(); }
    catch (err) { toast.error(fmtError(err)); }
  };

  return (
    <div className="mt-10" data-testid="admin-photo-moderation">
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-lg font-semibold text-[#1F2933] inline-flex items-center gap-2"><ImageIcon size={18} /> Photos de fiches signalées</h2>
        <span className="text-sm rounded-full px-3 py-0.5 border border-[#E4E7EB] bg-white" data-testid="photo-queue-counter">{data.queue_size} à traiter</span>
      </div>
      {data.pending.length === 0 && <div className="card p-4 text-sm text-[#3E4C59]" data-testid="photo-queue-empty">Aucun signalement en attente.</div>}
      <div className="space-y-2">
        {data.pending.map(p => <PhotoCard key={p.id} p={p} onHide={() => setHiding(p)} onOpen={() => setOpen(p)} />)}
      </div>
      {data.hidden.length > 0 && (
        <details className="mt-4">
          <summary className="text-sm text-[#3E4C59] cursor-pointer" data-testid="hidden-photos-toggle">{data.hidden.length} photo(s) masquée(s)</summary>
          <div className="space-y-2 mt-2">
            {data.hidden.map(p => <PhotoCard key={p.id} p={p} onUnhide={() => unhide(p)} onOpen={() => setOpen(p)} />)}
          </div>
        </details>
      )}
      {hiding && <HideModal photo={hiding} onCancel={() => setHiding(null)} onConfirm={hide} />}
      {open && <Lightbox url={open.full_url} legende={open.legende} onClose={() => setOpen(null)} />}
    </div>
  );
};
